import { useTranslation } from "react-i18next";
import InitContext from "../pages/InitContext";
import React, { useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InitRadioGroup from "./InitRadioGroup";
import GenesForSelecting from "./pgx/GenesForSelecting";
import Drugs from "./drugs/Drugs";
import Diseases from "./Diseases";
import WeightHeight from "./WeightHeight";
import { buildMeasurement } from "../pages/utils";
import { patientParameters } from "../../resources/json/jsons";
import AlcoholFrequency from "./AlcoholFrequency";
import PgxInit from "./pgx/PgxInit";
import useMmcService from "../../services/MmcService";
import ErrorMessage from "../errorMessage/ErrorMessage";
import Spinner from "../spinner/Spinner";
import { Consts } from "../pages/consts";
import PatientGenes from "../pgx/PatientGenes";
import { DEFAULT_PAT_GENES } from "../../resources/json/jsons";
import {Pgx} from "../pages";
import InitFooter from "./InitFooter";

const PatientInit = ({ onFinishInit, dbPatient }) => {
  const { t: t } = useTranslation();
  const { HEIGHT_DEFAULT, WEIGHT_DEFAULT } = Consts;
  const { gender, ethnicity, education, smoking_habit, drinking_habit } = dbPatient;
  const context = useContext(InitContext);
  const [myPatient, setMyPatient] = useState(dbPatient);
  const [initGender, setInitGender] = useState(gender);
  const [initEthnicity, setInitEthnicity] = useState(ethnicity);
  const [initEducation, setInitEducation] = useState(education);
  const [initSmoking, setInitSmoking] = useState(smoking_habit);
  const [initDrinking, setInitDrinking] = useState(drinking_habit);
  const [patHeight, setPatHeight] = useState(HEIGHT_DEFAULT);
  const [patWeight, setPatWeight] = useState(WEIGHT_DEFAULT);
  const [selectedValue, setSelectedValue] = useState(gender);
  const [patientParameter, setPatientParameter] = useState({});
  const [patientDiseases, setPatientDiseases] = useState([]);
  const [patientsDiseasesInDb, setPatientsDiseasesInDb] = useState([]);
  const [qrFiles, setQrFiles] = useState([]);
  const [pgxExists, setPgxExists] = useState(Consts.YES);
  const [newPgx, setNewPgx] = useState({ labName: "", testDate: "" }); // new pgx in init
  const [patGenes, setPatGenes] = useState(DEFAULT_PAT_GENES);
  const [patDrugs, setPatDrugs] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [availableGeneNames, setAvailableGeneNames] = useState([]);
  const [openIntakeDialog, setOpenIntakeDialog] = useState(false);
  const [openAlcoFrequencyPopup, setOpenAlcoFrequencyPopup] = useState(false);
  const [nextButtonEnabled, setNextButtonEnabled] = useState(true);

  const {
    loading,
    error,
    clearError,
    updateDbUser,
    saveDbDisease,
    saveDbGeneTest,
    saveDbDrugsQr,
    getDbDrugs,
    getDbGenes,
    saveDbMeasurement,
    getDbWeight,
    getDbHeight,
    getDbDiseases,
  } = useMmcService();

  useEffect(() => {
    if (!availableGeneNames || availableGeneNames?.length === 0) {
      setAvailableGeneNames(
        Consts.ALL_GENE_NAMES?.filter(
          (geneName) => !Consts.PATIENT_INITIAL_GENES?.includes(geneName),
        ),
      );
    }

    setPatientParameter(patientParameters[0]);
  }, []);

  useEffect(() => {
    clearError();

    getDbDrugs(context?.token, dbPatient?.id, false, true)
      .then((drugs) => setPatDrugs(drugs));
  }, []);

  useEffect(() => {
    clearError();

    getDbGenes(context?.token, dbPatient?.id)
      .then((genesInDb) => setPatGenes(genesInDb));
  }, []);

  useEffect(() => {
    clearError();

    if (dbPatient?.id) {
      getDbDiseases(context?.token, dbPatient?.id).then((data) => setPatientsDiseasesInDb(data?.diseases));
    }
  }, [dbPatient, patientDiseases]);

  useEffect(() => {
    updateNextButtonStatus();
  }, [currentIndex, newPgx, pgxExists, patGenes, patDrugs]);

  const handleRadioGroupValueChanging = (event) => {
    switch (patientParameter) {
      case patientParameters[0]: // gender
        setInitGender(event.target.value);
        break;
      case patientParameters[1]: //ethnicity
        setInitEthnicity(event.target.value);
        break;
      case patientParameters[2]: // education
        setInitEducation(event.target.value);
        break;
      case patientParameters[3]: // smoking
        setInitSmoking(event.target.value);
        break;
      case patientParameters[4]: // alcohol
        if (event.target.value === Consts.NEVER) {
          setMyPatient({ ...myPatient, drinking_frequency: "" });
        }

        setOpenAlcoFrequencyPopup(event.target.value !== Consts.NEVER);
        setInitDrinking(event.target.value);
        break;
      default:
        break;
    }

    setSelectedValue(event.target.value); // TODO CHECK
  };

  const saveDiseases = () => {
    clearError();

    patientDiseases?.forEach((disease) => {
      if (context?.token && context?.token !== "") {
        saveDbDisease(context?.token, disease, dbPatient?.id).then(() => {
          disease = { ...disease };
          setPatientsDiseasesInDb([...patientsDiseasesInDb, disease]);
          setPatientDiseases([]);
        });
      }
    });
  };

  const buildErrorMessage = (error) => {
    let message = t("error_by_retrieving_patients_data");

    if (error) {
      if (error?.includes(Consts.PUT)) {
        message = t("error_by_updating_patients_data");
      } else if (error?.includes(Consts.MEASUREMENT)) {
        message = t("error_by_saving_a_measurement");
      } else if (error?.includes(Consts.DISEASE)) {
        message = t("error_by_saving_a_disease");
      } else if (error?.includes(Consts.GENE_TEST)) {
        message = t("error_by_saving_a_pgx_test");
      } else if (error?.includes(Consts.INTERACTIONS) && error?.includes(Consts.CREATE)) {
        message = t("error_by_checking_drugs_for_interactions_in_abda_db");
      } else if (error?.includes("prescription")) {
        message = t("error_by_saving_a_drug");
      } else if (error?.includes(Consts.INTERACTIONS)) {
        message = t("error_by_retrieving_drug_drug_interactions_from_the_drug_database");
      } else if (error?.includes(Consts.BMP)) {
        message = t("error_by_uploading_the_qr_code_for_the_medication_plan");
      }
    }

    return message;
  };

  const handleSaveGeneTest = () => {
    if(pgxExists === Consts.YES && newPgx?.labName?.length > 0 && newPgx?.testDate?.length > 0
      && patGenes?.length > 0) {
      const pgxResults = {...newPgx, genes: patGenes};
      clearError();
      saveDbGeneTest(context?.token, pgxResults, dbPatient?.id).then();
    }
  };

  function uploadFile() {
    clearError();
    const formData = new FormData();
    formData?.append(Consts.FILE, qrFiles[0]);
    saveDbDrugsQr(context?.token, formData, dbPatient?.id).then();
  }

  const saveGender = () => {
    if (initGender !== myPatient?.gender) {
      const patientData = { ...myPatient, gender: initGender };
      setMyPatient(patientData);
      updatePatient(patientData);
    }

    setSelectedValue(initEthnicity);
  };

  const saveEthnicity = () => {
    if (initEthnicity !== myPatient?.ethnicity) {
      const patientData = { ...myPatient, ethnicity: initEthnicity };
      setMyPatient(patientData);
      updatePatient(patientData);
    }

    setSelectedValue(initEducation);
  };

  const saveEducation = () => {
    if (initEducation !== myPatient?.education) {
      const patientData = { ...myPatient, education: initEducation };
      setMyPatient(patientData);
      updatePatient(patientData);
    }

    setSelectedValue(initSmoking);
  };

  const saveSmoking = () => {
    if (initSmoking !== myPatient?.smoking_habit) {
      const patientData = { ...myPatient, smoking_habit: initSmoking };
      setMyPatient(patientData);
      updatePatient(patientData);
    }

    setSelectedValue(initDrinking);
  };

  const saveDrinking = () => {
    if (initDrinking !== myPatient?.drinking_habit) {
      const patientData = { ...myPatient, drinking_habit: initDrinking };
      setMyPatient(patientData);
      updatePatient(patientData);
    }

    setSelectedValue(initDrinking);
  };

  const handleLastStep = () => {
    if (qrFiles?.length > 0) {
      uploadFile();
    }

    const patientData = {...myPatient, registration_date: new Date().toISOString()?.substring(0, 10)};
    setMyPatient(patientData);
    updatePatient(patientData);
    onFinishInit(patientData);
  };

  const saveWeightHeight = () => {
    clearError();

    getDbWeight(context?.token, dbPatient?.id).then((measurement) => {
      if (measurement !== patWeight) {
        clearError();
        const weightJson = buildMeasurement(Consts.WEIGHT, patWeight, Consts.KG);
        saveDbMeasurement(context?.token, dbPatient?.id, weightJson).then();
      }
    });

    clearError();

    getDbHeight(context?.token, dbPatient?.id).then((measurement) => {
      if (measurement !== patHeight) {
        clearError();
        const heightJson = buildMeasurement(Consts.HEIGHT, patHeight, Consts.CM);
        saveDbMeasurement(context?.token, dbPatient?.id, heightJson).then();
      }
    });
  };

  const handleNext = () => {
    let newIndex = currentIndex + 1;

    if (currentIndex === 0) {
      saveGender();

      if(dbPatient?.study_id !== Consts.ETHNICITY_STUDY
        && dbPatient?.study_id !== Consts.ETHNICITY_STUDY2
        && dbPatient?.study_id !== Consts.DEMO_HOSPITAL){
        newIndex = newIndex + 1;
        setSelectedValue(initEducation);
      } else {
        setSelectedValue(initEthnicity);
      }
    } else if (currentIndex === 1) {
      saveEthnicity();
    } else if (currentIndex === 2) {
      saveEducation();
    } else if (currentIndex === 3) {
      saveSmoking();
    } else if (currentIndex === 4) {
      saveDrinking();
      newIndex = 6; // skip 5 - the frequency for the alcohol since it is included in view 4 for alcohol
    } else if (currentIndex === 6) {
      saveWeightHeight();
    } else if (currentIndex === 7) {
      saveDiseases();
    } else if (currentIndex === 8 && patGenes) {
      newIndex = 11; // skip steps 9-11 because gene test exists in the DB
    } else if (currentIndex === 10) {
      handleSaveGeneTest();
    } else if (currentIndex === 11) {
      handleLastStep();
    } else if (currentIndex === 8 && pgxExists !== Consts.YES) {
      newIndex = 11; // skip steps 10-11 because gene test results are not available
    }

    setCurrentIndex(newIndex);
    setPatientParameter(patientParameters[newIndex]);
  };

  const handleBack = () => {
    let newIndex = currentIndex - 1;

    if (currentIndex === 1) {
      setSelectedValue(initGender);
    } else if (currentIndex === 2) {
      setSelectedValue(initEthnicity);
    } else if (currentIndex === 3) {
      if(dbPatient?.study_id !== Consts.ETHNICITY_STUDY
        && dbPatient?.study_id !== Consts.ETHNICITY_STUDY2
        && dbPatient?.study_id !== Consts.DEMO_HOSPITAL){
        newIndex = newIndex - 1;
        setSelectedValue(initGender);
      } else {
        setSelectedValue(initEducation);
      }
    } else if (currentIndex === 4) {
      setSelectedValue(initSmoking);
    } else if (currentIndex === 5) {
      setSelectedValue(initDrinking);
    } else if (currentIndex === 11 && pgxExists !== Consts.YES && !patGenes) {
      // pgx - 8, drugs - 11
      setPgxExists(Consts.NOT);
      newIndex = 8; // back to the start of the yes/no question
    } else if (currentIndex === 11 && patGenes) {
      newIndex = 8; // skip 10 and 9: PGx from the DB is shown in the index 8 -
    } else if (currentIndex === 6) {
      newIndex = 4; // skip 5 - the frequency for the alcohol since it is included in view 4 for alcohol
    }

    setCurrentIndex(newIndex);
    setPatientParameter(patientParameters[newIndex]);
  };

  const handlePgxExists = (value) => {
    setPgxExists(value);
  };

  const handleSaveAlcoFrequency = (frequency) => {
    if (!frequency) {
      setInitDrinking(Consts.NEVER);
      setSelectedValue(Consts.NEVER);
    }

    if (frequency !== myPatient?.drinking_frequency) {
      const patientData = { ...myPatient, drinking_frequency: frequency };
      setMyPatient(patientData);
      updatePatient(patientData);
    }

    setOpenAlcoFrequencyPopup(false);
  };

  const updatePatient = (patData) => {
    if (context?.token && context?.token !== "") {
      clearError();
      updateDbUser(context?.token, patData, dbPatient?.id).then();
    } else {
      console.error("updatePatient, the token is wrong");
    }
  };

  const updateNextButtonStatus = () => {
    setNextButtonEnabled(true);

    if (currentIndex === 8) {
      if (pgxExists === Consts.YES && (!newPgx?.labName || !newPgx?.testDate)) {
        setNextButtonEnabled(false);
      }
    } else if (currentIndex === 9) {
      if (patGenes?.length === 0) {
        setNextButtonEnabled(false);
      }
    } else if (currentIndex === 11) {
      if (!patDrugs?.every((product) => product?.intakes?.length > 0 || product?.intakeType === Consts.ON_DEMAND)) {
        setNextButtonEnabled(false);
      }
    }
  };

  const handleInitNewTest = (newGenes) => {
    setNewPgx((prev) => ({...prev, genes: newGenes}));
  }

  const handleSetNewPgx = (newTest) => {
    setNewPgx({...newTest, genes: DEFAULT_PAT_GENES});
  }

  const errorMessageComp = error && (
    <ErrorMessage message={buildErrorMessage(error)} />
  );

  const spinner = loading && <Spinner />;
  let diseasesToShow = [];

  if (patientsDiseasesInDb?.length > 0) {
    diseasesToShow = [...patientsDiseasesInDb];
  }

  if (patientDiseases?.length > 0) {
    diseasesToShow = [...diseasesToShow, ...patientDiseases];
  }

  return (
    <div id="patient-init">
      <div className="w3-display-container content-container">
        <div style={{ width: '100%', textAlign: 'right', paddingRight: "20px" }}>
          <label>{t("username")}: {dbPatient?.login}, {t("role")}: {t("patient").toLowerCase()}</label>
        </div>
        { patientParameter?.paramName === Consts.PGX && patGenes?.length > 0
          ? <Pgx init={ true } printView={ false } onHandleBack={ handleBack } onFinishInit={ onFinishInit }
                 onHandleNext={handleNext} patientId={dbPatient?.id}/>
          : <div className="w3-display-middle form-display">
              {patientParameter?.paramName === "gender" && (
                <div className="w3-panel w3-padding-small w3-center info-panel">
                  <h2 className="info-panel-text">{t("patient_init_info")}.</h2>
                </div>
              )}
              <div className="w3-panel w3-card-4 form-panel">
                {errorMessageComp}
                {spinner}
                {patientParameter?.paramName !== Consts.PGX_GENES &&
                  !openIntakeDialog && (
                    <header className="w3-container w3-center w3-padding-small">
                      <p>
                        {patientParameter?.paramIcon && (
                          <FontAwesomeIcon icon={patientParameter.paramIcon} />
                        )}{" "}
                        <label className="bold-label">
                          {t(patientParameter?.paramTitle)}
                        </label>
                      </p>
                    </header>
                  )}
                <>
                  {patientParameter?.paramRadios?.length > 0 && patientParameter?.paramName !== Consts.PGX && (
                      <div className="w3-container form-content">
                        <InitRadioGroup
                          patientParameter={patientParameter}
                          selectedValue={selectedValue}
                          handleChange={handleRadioGroupValueChanging}
                          newLabel={
                            patientParameter?.paramName === Consts.ALCOHOL &&
                            myPatient?.drinking_frequency &&
                            myPatient?.drinking_frequency !== ""
                              ? `${t(selectedValue)}, ${t(
                                myPatient?.drinking_frequency,
                              )?.toLowerCase()}`
                              : null
                          }
                          labelPrefix={selectedValue}
                        />
                      </div>
                    )
                  }
                  {patientParameter?.paramName === Consts.WEIGHT_HEIGHT && (
                    <WeightHeight patWeight={patWeight} patHeight={patHeight} setPatWeight={setPatWeight}
                                  setPatHeight={setPatHeight} />
                  )}
                  {patientParameter?.paramName === Consts.DISEASES && (
                    <Diseases
                      patientDiseases={patientDiseases}
                      onSetPatientDiseases={setPatientDiseases}
                      diseasesToShow={diseasesToShow}
                      setPatientsDiseasesInDb={setPatientsDiseasesInDb}
                    />
                  )}
                  {patientParameter?.paramName === Consts.PGX  && !patGenes?.length && (
                    <PgxInit pgxExists={pgxExists} onChangePgxExists={handlePgxExists} newPgx={newPgx}
                             setNewPgx={handleSetNewPgx} />
                  )}
                  {patientParameter?.paramName === Consts.PGX_GENES  && !patGenes?.length && (
                    <GenesForSelecting
                      patGenes={newPgx?.genes}
                      onHandleInitNewTest={handleInitNewTest}
                      availableGeneNames={availableGeneNames}
                      setAvailableGeneNames={setAvailableGeneNames}
                    />
                  )}
                  {patientParameter?.paramName === Consts.PGX_SELECTED_GENES && (
                    <PatientGenes patGenes={patGenes ? patGenes : newPgx?.genes} setPatGenes={setPatGenes} init={ true }
                                  patientId={dbPatient?.id} deleteVisible={ false } />
                  )}
                  {patientParameter?.paramName === Consts.DRUGS && (
                    <Drugs
                      patDrugs={patDrugs}
                      setPatDrugs={setPatDrugs}
                      setQrFiles={setQrFiles}
                      openIntakeDialog={openIntakeDialog}
                      setOpenIntakeDialog={(value) => setOpenIntakeDialog(value)}
                    />
                  )}
                </>
                {!openIntakeDialog && (
                  <InitFooter parameter={patientParameter?.paramName} onHandleBack={handleBack} myPatient={myPatient}
                              onFinishInit={onFinishInit}  onHandleNext={handleNext}
                              nextButtonEnabled={nextButtonEnabled} />
                )}
              </div>
            </div>
        }
      </div>
      {openAlcoFrequencyPopup && (
        <AlcoholFrequency
          onSaveAlcoFrequency={handleSaveAlcoFrequency}
          showDialog={openAlcoFrequencyPopup}
        />
      )}
    </div>
  );
};

export default PatientInit;
